import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// App Logo URL
const APP_LOGO = "https://customer-assets.emergentagent.com/job_trend-stock-app-1/artifacts/tmh7qekk_ChatGPT%20Image%20Feb%2025%2C%202026%2C%2001_23_15%20PM.png";

// Navbar Component
const Navbar = () => (
  <nav className="fixed top-0 left-0 right-0 z-50 bg-[#030712]/80 backdrop-blur-xl border-b border-white/5">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
          <img src={APP_LOGO} alt="StockTrendFusion" className="w-10 h-10 rounded-xl" />
          <span className="font-semibold text-lg text-white">StockTrendFusion</span>
        </Link>
        <Link 
          to="/" 
          className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
          data-testid="nav-back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
      </div>
    </div>
  </nav>
);

// Footer
const Footer = () => (
  <footer className="py-12 border-t border-white/5" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <img src={APP_LOGO} alt="StockTrendFusion" className="w-10 h-10 rounded-xl" />
          <span className="font-semibold text-white">StockTrendFusion</span>
        </div>
        
        <div className="flex items-center gap-8">
          <Link 
            to="/support" 
            className="text-sm text-zinc-400 hover:text-white transition-colors"
            data-testid="footer-support-link"
          >
            Support
          </Link>
          <Link 
            to="/privacy" 
            className="text-sm text-zinc-400 hover:text-white transition-colors"
            data-testid="footer-privacy-link"
          >
            Privacy Policy
          </Link>
          <Link 
            to="/terms" 
            className="text-sm text-zinc-400 hover:text-white transition-colors"
            data-testid="footer-terms-link"
          >
            Terms of Service
          </Link>
        </div>
        
        <p className="text-sm text-zinc-500">
          &copy; {new Date().getFullYear()} StockTrendFusion. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

const PrivacyPage = () => {
  return (
    <div className="min-h-screen" data-testid="privacy-page">
      <Navbar />
      
      {/* Header */}
      <section className="pt-32 pb-12 relative">
        <div className="absolute inset-0 grid-pattern opacity-30" />
        <div className="max-w-3xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-sm font-medium tracking-wider uppercase text-emerald-400 mb-4">
              Legal
            </p>
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
              Privacy Policy
            </h1>
            <p className="text-zinc-400">
              Last updated: December 2025
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Content */}
      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <motion.div 
            className="prose-dark space-y-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">Introduction</h2>
              <p className="text-zinc-400 leading-relaxed">
                StockTrendFusion ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and related services.
              </p>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">Information We Collect</h2>
              <h3 className="text-xl font-medium text-white mb-3">Personal Information</h3>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We may collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="text-zinc-400 space-y-2 list-disc pl-6">
                <li>Register for an account</li>
                <li>Use our app features</li>
                <li>Contact our support team</li>
                <li>Subscribe to our services</li>
              </ul>
              
              <h3 className="text-xl font-medium text-white mb-3 mt-6">Automatically Collected Information</h3>
              <p className="text-zinc-400 leading-relaxed">
                When you use our app, we may automatically collect certain information including device information, usage data, and analytics to improve our services.
              </p>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">How We Use Your Information</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We use the information we collect to:
              </p>
              <ul className="text-zinc-400 space-y-2 list-disc pl-6">
                <li>Provide and maintain our services</li>
                <li>Personalize your experience</li>
                <li>Process transactions and send notifications</li>
                <li>Analyze usage patterns to improve our app</li>
                <li>Communicate with you about updates and offers</li>
                <li>Ensure security and prevent fraud</li>
              </ul>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">Data Security</h2>
              <p className="text-zinc-400 leading-relaxed">
                We implement industry-standard security measures to protect your personal information. This includes 256-bit AES encryption for data at rest and in transit, secure authentication protocols, and regular security audits. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">Third-Party Services</h2>
              <p className="text-zinc-400 leading-relaxed">
                Our app may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to read their privacy policies before providing any personal information.
              </p>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">Data Retention</h2>
              <p className="text-zinc-400 leading-relaxed">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required by law. You may request deletion of your data at any time by contacting our support team.
              </p>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">Your Rights</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                Depending on your location, you may have certain rights regarding your personal information:
              </p>
              <ul className="text-zinc-400 space-y-2 list-disc pl-6">
                <li>Access and receive a copy of your data</li>
                <li>Correct inaccurate information</li>
                <li>Request deletion of your data</li>
                <li>Opt-out of marketing communications</li>
                <li>Data portability</li>
              </ul>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">Children's Privacy</h2>
              <p className="text-zinc-400 leading-relaxed">
                Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">Changes to This Policy</h2>
              <p className="text-zinc-400 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date. Your continued use of our services after any changes constitutes acceptance of the updated policy.
              </p>
            </div>
            
            <div>
              <h2 className="text-2xl font-semibold text-white mb-4">Contact Us</h2>
              <p className="text-zinc-400 leading-relaxed">
                If you have questions about this Privacy Policy or our data practices, please contact us through our{" "}
                <Link to="/support" className="text-emerald-400 hover:text-emerald-300 underline">
                  Support Page
                </Link>.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default PrivacyPage;
